import { useParams } from '@tanstack/react-router';
import {
	AdditionalRequirement,
	type AddressDTO,
	ContainerSizeEnum,
	PriceCalculatorTransportType,
	type PriceRequestDto,
	type ShippingLocationRequestDto,
	useSavePriceRequest,
} from '@uturn/api/v1';
import { Page } from '@uturn/portal/components';
import {
	type LocationField,
	PriceRequestForm,
	type PriceRequestFormFields,
	PriceRequestLeadForm,
	PublicPriceCalculatorResultById,
	RouteMap,
} from '@uturn/portal/modules/price-calculator/components';
import { PriceCalculatorContext } from '@uturn/portal/modules/price-calculator/context/price-calculator-context';
import { Logo } from '@uturn/ui-kit';
import { AnimatePresence } from 'framer-motion';
import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const defaultValues: PriceRequestDto = {
	transportType: PriceCalculatorTransportType.IMPORT,
	containerSize: ContainerSizeEnum.UNKNOWN,
	additionalRequirement: AdditionalRequirement.NONE,
	locations: [{}, {}, {}],
	email: '',
};

export function PublicPriceCalculator() {
	const { t } = useTranslation();
	const { uuid } = useParams({ from: '/_public/price-calculator/{-$uuid}' });
	const { mutate } = useSavePriceRequest();

	const [locations, setLocations] = useState<AddressDTO[]>([]);
	const [openLeadModal, setOpenLeadModal] = useState(false);

	const priceRequest = useRef<PriceRequestFormFields>(defaultValues);

	const handleEmailSubmit = (email: string) => {
		const dtoLocations: ShippingLocationRequestDto[] =
			priceRequest.current.locations.map((location: LocationField) => ({
				id: location.id ?? undefined,
				...(location.googlePlaceId && {
					googlePlaceId: location.googlePlaceId,
				}),
			}));
		const data: PriceRequestDto = {
			...priceRequest.current,
			locations: dtoLocations,
			email,
		};

		mutate(
			{ data },
			{
				onSuccess: () => {
					window.location.replace(
						'https://uturn-now.com/en/price-calculator-thank-you',
					);
				},
			},
		);
	};

	const onSubmit = (request: PriceRequestFormFields) => {
		priceRequest.current = request;

		setOpenLeadModal(true);
	};

	// const onReset = () => {
	// 	priceRequest.current = defaultValues;
	// 	setLocations([]);
	// };

	return (
		<>
			<Helmet
				title={t('pages.price-calculator.title', 'Price Calculator').toString()}
			/>
			<div className="mx-auto px-4 py-8 sm:px-6 sm:py-10 lg:p-10 2xl:max-w-[90%]">
				<Logo className="mb-7 h-8 w-auto" />
				<Page
					title={t(
						'pages.price-calculator.title',
						'Price Calculator',
					).toString()}
				>
					<PriceCalculatorContext.Provider
						value={{
							locations,
							setLocations: (data) => setLocations(data),
						}}
					>
						<div className="relative grid grid-cols-[minmax(0,3fr),_minmax(0,2fr)] gap-2.5">
							<div className="flex w-full flex-col gap-8">
								<AnimatePresence mode="wait">
									{uuid === undefined ? (
										<PriceRequestForm
											defaultValues={defaultValues}
											onSubmit={onSubmit}
											onTripChange={setLocations}
										/>
									) : (
										<PublicPriceCalculatorResultById />
									)}
								</AnimatePresence>
							</div>
							<div className="relative h-full">
								<RouteMap
									locations={locations
										.filter(
											(location) => !!location.latitude && !!location.longitude,
										)
										.map((location) => ({
											lat: location.latitude as number, // Guarantied by Type Predicate Inference
											lng: location.longitude as number, // Guarantied by Type Predicate Inference
										}))}
								/>
							</div>
						</div>
					</PriceCalculatorContext.Provider>
					<PriceRequestLeadForm
						openModal={openLeadModal}
						setOpenModal={setOpenLeadModal}
						onSubmit={handleEmailSubmit}
					/>
				</Page>
			</div>
		</>
	);
}
